import React, { useEffect, useRef, useState, useMemo } from 'react';
import '../Style/Me.css';
import { useLanguage } from '../Context/Languagecontext';

// Structural design and animation layout schema metadata kept separate from content translations
const PILLARS_LAYOUT_CONFIG = [
  { id: 'p1', num: '01', heightClass: 'pillar-tall', delay: 0.05 },
  { id: 'p2', num: '02', heightClass: 'pillar-mid',  delay: 0.15 },
  { id: 'p3', num: '03', heightClass: 'pillar-mid',  delay: 0.25 },
  { id: 'p4', num: '04', heightClass: 'pillar-tall', delay: 0.35 },
];

/* Vector assets retained within local static layout structure mappings */
const ICONS = {
  p1: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.8" />
      <path d="M13 20.5L18 25L27 15.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  p2: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="16" width="28" height="18" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M6 16 L14 10 L20 16 L26 10 L34 16" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="15" y="24" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="1.6"/>
      <line x1="11" y1="20" x2="11" y2="26" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <line x1="29" y1="20" x2="29" y2="26" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  p3: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="13" r="5" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M10 34c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="8" cy="16" r="3.5" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M2 34c0-4 2.7-7.2 6-7.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="32" cy="16" r="3.5" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M38 34c0-4-2.7-7.2-6-7.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  p4: (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 30H32" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 26L17 20L22 24L30 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M25 14H30V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

export default function ManufacturingExcellence() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const { t, lang } = useLanguage();
  const mfgData = t.manufacturingExcellence;

  /* ── Structural Assembly of Localized Component Arrays ── */
  const localizedPillars = useMemo(() => {
    const rawPillarData = mfgData?.pillars || {};
    return PILLARS_LAYOUT_CONFIG.map((layout) => {
      const copy = rawPillarData[layout.id] || {};
      return {
        ...layout,
        heading: copy.heading || '',
        tagline: copy.tagline || '',
        body: copy.body || '',
      };
    });
  }, [mfgData]);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => { 
        if (entry.isIntersecting) { 
          setVisible(true); 
          io.disconnect(); 
        } 
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <section className={`mfg-section ${lang === 'ta' ? 'mfg-section--tamil' : ''}`} ref={sectionRef}>
      <div className="mfg-inner">

        {/* ── Header ── */}
        <header className={`mfg-header ${visible ? 'mfg-in' : ''}`}>
          <p className="mfg-eyebrow">{mfgData?.eyebrow}</p>
          <h2 className="mfg-title">
            {mfgData?.titleNormal}<em>{mfgData?.titleEmphasized}</em>
          </h2>
          <p className="mfg-subtitle">{mfgData?.subtitle}</p>
        </header>

        {/* ── Arc Stage ── */}
        <div className="mfg-stage">
          {/* Curved dynamic graphic structure */}
          <div className="mfg-arc-backdrop" aria-hidden="true">
            <svg viewBox="0 0 1100 320" preserveAspectRatio="xMidYMax meet" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="arcGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(244,199,100,0.10)"/>
                  <stop offset="100%" stopColor="rgba(245,237,207,0.55)"/>
                </linearGradient>
              </defs>
              <path
                d="M550,340 L50,60 Q180,-40 550,10 Q920,-40 1050,60 Z"
                fill="url(#arcGrad)"
                stroke="rgba(244,199,100,0.18)"
                strokeWidth="1"
              />
            </svg>
          </div>

          {/* Pillars row layout configuration mapping iteration */}
          <div className="mfg-pillars">
            {localizedPillars.map((p) => (
              <div
                key={p.id}
                className={`mfg-pillar ${p.heightClass} ${visible ? 'mfg-in' : ''}`}
                style={{ transitionDelay: `${p.delay + 0.2}s` }}
              >
                <div className="mfg-icon-wrap">
                  <div className="mfg-icon-circle">
                    <span className="mfg-icon-svg">{ICONS[p.id]}</span>
                  </div>
                  <div className="mfg-icon-orbit" aria-hidden="true" />
                </div>
                <div className="mfg-pillar-text">
                  <span className="mfg-pillar-num">{p.num}</span>
                  <h3 className="mfg-pillar-heading">{p.heading}</h3>
                  <p className="mfg-pillar-tagline">{p.tagline}</p>
                  <p className="mfg-pillar-body">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Bottom Badges ── */}
        <div className={`mfg-badges ${visible ? 'mfg-in' : ''}`}>
          {mfgData?.badges?.map((badgeText) => (
            <span key={badgeText} className="mfg-badge">
              <span className="mfg-badge-dot" aria-hidden="true" />
              {badgeText}
            </span>
          ))}
        </div>

      </div>
    </section>
  );
}